import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  isInvoiceAvailable,
  openInvoiceUrl,
  useTelegramUser,
  type InvoiceStatus,
} from '@/shared/telegram';

import { AppError, env, isAppError } from '@/shared/api';
import type { CreateSubscriptionCheckoutResponse } from '@/shared/api';
import { createSupportToastAction, emitToast } from '@/shared/lib/toastEvents';

import {
  POLLING_REQUEST_TIMEOUT_MS,
  subscriptionKeys,
  subscriptionQueryOptions,
  useCreateSubscriptionCheckout,
} from '../api';
import {
  reconcileCheckout,
  type CheckoutReconciliationAction,
  type CheckoutStatusProbe,
} from '../model';

export type SubscriptionPaymentPhase =
  | 'idle'
  | 'creatingCheckout'
  | 'waitingInvoice'
  | 'openingInvoice'
  | 'confirmingSubscription'
  | 'awaitingConfirmation'
  | 'confirmationNeedsSupport';

interface SubscribeWithStarsResult {
  checkoutId: string;
  invoiceStatus: InvoiceStatus;
  subscriptionActivated: boolean;
}

type PendingPaymentStage = 'checkout' | 'confirmation';

interface StoredPendingPayment {
  checkoutId: string;
  stage: PendingPaymentStage;
  expiresAt: number;
}

interface PaymentLease {
  token: string;
  expiresAt: number;
}

type PaymentStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const INVOICE_POLL_DELAYS_MS = [500, 1000, 1500, 2500, 4000, 6000, 8000, 8000] as const;
const SUBSCRIPTION_POLL_DELAYS_MS = [500, 1000, 1500, 2500, 4000, 6000, 8000] as const;
const BACKGROUND_CONFIRMATION_POLL_MS = 30_000;
const NEEDS_SUPPORT_POLL_MS = 5 * BACKGROUND_CONFIRMATION_POLL_MS;
const CHECKOUT_STORAGE_TTL_MS = 15 * 60 * 1000;
const CONFIRMATION_STORAGE_TTL_MS = 30 * 60 * 1000;
const PAYMENT_LEASE_TTL_MS = 60 * 1000;
const PAYMENT_LEASE_RENEW_MS = 20 * 1000;
const PENDING_PAYMENT_STORAGE_PREFIX = 'shanti:subscription-payment';
const PAYMENT_LOCK_PREFIX = 'shanti:subscription-payment-lock';
const heldPaymentLeaseTokens = new Map<string, string>();

const pendingPaymentStorageKey = (ownerKey: string): string =>
  `${PENDING_PAYMENT_STORAGE_PREFIX}:${ownerKey}`;

const paymentLeaseStorageKey = (ownerKey: string): string => `${PAYMENT_LOCK_PREFIX}:${ownerKey}`;

const getPaymentStorage = (): PaymentStorage | null => {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const clearStoredPendingPayment = (ownerKey: string, expectedCheckoutId?: string): void => {
  try {
    const storage = getPaymentStorage();
    if (!storage) return;
    const key = pendingPaymentStorageKey(ownerKey);

    if (expectedCheckoutId) {
      const rawValue = storage.getItem(key);
      if (!rawValue) return;
      const value: unknown = JSON.parse(rawValue);
      if (isStoredPendingPayment(value) && value.checkoutId !== expectedCheckoutId) return;
    }

    storage.removeItem(key);
  } catch {
    // Storage is only a safety net; the in-memory flow can continue without it.
  }
};

const isStoredPendingPayment = (value: unknown): value is StoredPendingPayment => {
  if (typeof value !== 'object' || value === null) return false;
  if (!('checkoutId' in value) || typeof value.checkoutId !== 'string') return false;
  if (!('stage' in value) || (value.stage !== 'checkout' && value.stage !== 'confirmation')) {
    return false;
  }
  return (
    'expiresAt' in value && typeof value.expiresAt === 'number' && Number.isFinite(value.expiresAt)
  );
};

const loadStoredPendingPayment = (ownerKey: string | null): StoredPendingPayment | null => {
  if (!ownerKey) return null;
  const storage = getPaymentStorage();
  if (!storage) return null;

  try {
    const rawValue = storage.getItem(pendingPaymentStorageKey(ownerKey));
    if (!rawValue) return null;

    const value: unknown = JSON.parse(rawValue);
    if (
      !isStoredPendingPayment(value) ||
      (value.stage === 'checkout' && value.expiresAt <= Date.now())
    ) {
      clearStoredPendingPayment(ownerKey);
      return null;
    }
    return value;
  } catch {
    clearStoredPendingPayment(ownerKey);
    return null;
  }
};

const storePendingPayment = (
  ownerKey: string | null,
  checkoutId: string,
  stage: PendingPaymentStage
): StoredPendingPayment => {
  const ttlMs = stage === 'confirmation' ? CONFIRMATION_STORAGE_TTL_MS : CHECKOUT_STORAGE_TTL_MS;
  const value: StoredPendingPayment = {
    checkoutId,
    stage,
    expiresAt: Date.now() + ttlMs,
  };

  try {
    if (ownerKey) {
      getPaymentStorage()?.setItem(pendingPaymentStorageKey(ownerKey), JSON.stringify(value));
    }
  } catch {
    // Storage is only a safety net; keep the current session protected in memory.
  }
  return value;
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const parsePaymentLease = (rawValue: string | null): PaymentLease | null => {
  if (!rawValue) return null;
  try {
    const value: unknown = JSON.parse(rawValue);
    if (typeof value !== 'object' || value === null) return null;
    if (!('token' in value) || typeof value.token !== 'string') return null;
    if (!('expiresAt' in value) || typeof value.expiresAt !== 'number') return null;
    return { token: value.token, expiresAt: value.expiresAt };
  } catch {
    return null;
  }
};

const acquirePaymentLease = async (
  ownerKey: string
): Promise<{ storage: PaymentStorage; key: string; token: string } | null> => {
  const storage = getPaymentStorage();
  if (!storage) return null;

  const key = paymentLeaseStorageKey(ownerKey);
  const token = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

  while (true) {
    try {
      const currentLease = parsePaymentLease(storage.getItem(key));
      if (!currentLease || currentLease.expiresAt <= Date.now()) {
        storage.setItem(
          key,
          JSON.stringify({ token, expiresAt: Date.now() + PAYMENT_LEASE_TTL_MS })
        );
        await delay(50);

        const confirmedLease = parsePaymentLease(storage.getItem(key));
        if (confirmedLease?.token === token) return { storage, key, token };
      }
    } catch {
      return null;
    }

    await delay(150 + Math.floor(Math.random() * 100));
  }
};

const runWithStoragePaymentLease = async <T>(
  ownerKey: string,
  action: () => Promise<T>
): Promise<T> => {
  const lease = await acquirePaymentLease(ownerKey);
  if (!lease) {
    emitToast({
      type: 'error',
      message: 'Payment could not be started safely in this browser. Try reopening Telegram.',
    });
    throw new AppError('Payment coordination is unavailable', 'client');
  }
  heldPaymentLeaseTokens.set(ownerKey, lease.token);

  const renewLease = (): void => {
    try {
      const currentLease = parsePaymentLease(lease.storage.getItem(lease.key));
      if (currentLease?.token !== lease.token) return;
      lease.storage.setItem(
        lease.key,
        JSON.stringify({ token: lease.token, expiresAt: Date.now() + PAYMENT_LEASE_TTL_MS })
      );
    } catch {
      // The primary Web Lock path is used when available; storage lease is best-effort fallback.
    }
  };

  const renewalIntervalId = window.setInterval(renewLease, PAYMENT_LEASE_RENEW_MS);
  try {
    return await action();
  } finally {
    window.clearInterval(renewalIntervalId);
    if (heldPaymentLeaseTokens.get(ownerKey) === lease.token) {
      heldPaymentLeaseTokens.delete(ownerKey);
    }
    try {
      const currentLease = parsePaymentLease(lease.storage.getItem(lease.key));
      if (currentLease?.token === lease.token) lease.storage.removeItem(lease.key);
    } catch {
      // A stale lease expires automatically.
    }
  }
};

const ensurePaymentLockOwnership = async (ownerKey: string | null): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.locks) return true;
  if (!ownerKey) return false;

  const token = heldPaymentLeaseTokens.get(ownerKey);
  const storage = getPaymentStorage();
  if (!token || !storage) return false;

  const key = paymentLeaseStorageKey(ownerKey);
  try {
    const currentLease = parsePaymentLease(storage.getItem(key));
    if (currentLease && currentLease.token !== token && currentLease.expiresAt > Date.now()) {
      return false;
    }

    storage.setItem(key, JSON.stringify({ token, expiresAt: Date.now() + PAYMENT_LEASE_TTL_MS }));
    await delay(50);
    return parsePaymentLease(storage.getItem(key))?.token === token;
  } catch {
    return false;
  }
};

const runWithPaymentLock = <T>(ownerKey: string | null, action: () => Promise<T>): Promise<T> => {
  if (!ownerKey) {
    emitToast({
      type: 'error',
      message: 'Telegram user could not be identified. Try reopening the application.',
    });
    return Promise.reject(new AppError('Payment owner is unavailable', 'client'));
  }
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks
      .request<Promise<T>>(`${PAYMENT_LOCK_PREFIX}:${ownerKey}`, action)
      .then((result) => result);
  }
  return runWithStoragePaymentLease(ownerKey, action);
};

const assertInvoiceAvailable = (): void => {
  if (env.useMock || isInvoiceAvailable()) return;

  emitToast({
    type: 'error',
    message: 'Telegram Stars payments are unavailable. Update Telegram and try again.',
  });
  throw new AppError('Telegram Stars payments are unavailable', 'client');
};

const openSubscriptionInvoice = async (invoiceLink: string): Promise<InvoiceStatus> => {
  if (env.useMock) {
    await delay(250);
    return 'paid';
  }

  try {
    return await openInvoiceUrl(invoiceLink);
  } catch (error) {
    emitToast({
      type: 'info',
      message:
        'Telegram did not return a payment status. We will keep checking to prevent a duplicate charge.',
      durationMs: 6000,
    });
    throw error;
  }
};

const isTerminalInvoiceLookupError = (error: unknown): boolean =>
  isAppError(error) &&
  (error.kind === 'auth' || error.kind === 'client' || error.kind === 'paymentRequired');

export function useSubscribeWithStars() {
  const queryClient = useQueryClient();
  const { mutateAsync: createCheckout } = useCreateSubscriptionCheckout();
  const telegramUser = useTelegramUser();
  const [paymentOwnerKey] = useState<string | null>(() =>
    telegramUser ? String(telegramUser.id) : null
  );
  const [restoredPayment] = useState<StoredPendingPayment | null>(() =>
    loadStoredPendingPayment(paymentOwnerKey)
  );
  const pendingCheckoutRef = useRef<CreateSubscriptionCheckoutResponse | null>(
    restoredPayment
      ? {
          checkoutId: restoredPayment.checkoutId,
          invoiceLink: null,
        }
      : null
  );
  const pendingPaymentStageRef = useRef<PendingPaymentStage | null>(restoredPayment?.stage ?? null);
  const pendingPaymentExpiresAtRef = useRef<number | null>(restoredPayment?.expiresAt ?? null);
  const inFlightRef = useRef<Promise<SubscribeWithStarsResult> | null>(null);
  const paidObservedAtRef = useRef<number | null>(null);
  const needsSupportNotifiedRef = useRef(false);
  const [phase, setPhase] = useState<SubscriptionPaymentPhase>(
    restoredPayment?.stage === 'confirmation' ? 'awaitingConfirmation' : 'idle'
  );

  const clearPendingPayment = useCallback(() => {
    const checkoutId = pendingCheckoutRef.current?.checkoutId;
    pendingCheckoutRef.current = null;
    pendingPaymentStageRef.current = null;
    pendingPaymentExpiresAtRef.current = null;
    paidObservedAtRef.current = null;
    needsSupportNotifiedRef.current = false;
    if (paymentOwnerKey) clearStoredPendingPayment(paymentOwnerKey, checkoutId);
  }, [paymentOwnerKey]);

  const rememberPendingPayment = useCallback(
    (checkoutId: string, stage: PendingPaymentStage): void => {
      const storedPayment = storePendingPayment(paymentOwnerKey, checkoutId, stage);
      pendingPaymentStageRef.current = stage;
      pendingPaymentExpiresAtRef.current = storedPayment.expiresAt;
    },
    [paymentOwnerKey]
  );

  const waitForInvoiceLink = useCallback(
    async (checkout: CreateSubscriptionCheckoutResponse): Promise<string> => {
      if (checkout.invoiceLink) return checkout.invoiceLink;

      setPhase('waitingInvoice');
      emitToast({
        type: 'info',
        message: 'Preparing payment link...',
      });

      let lastError: unknown;
      let receivedResponse = false;

      for (const delayMs of INVOICE_POLL_DELAYS_MS) {
        await delay(delayMs);

        try {
          const invoice = await queryClient.fetchQuery(
            subscriptionQueryOptions.invoice(checkout.checkoutId)
          );
          receivedResponse = true;

          if (invoice?.invoiceLink) {
            pendingCheckoutRef.current = {
              ...checkout,
              invoiceLink: invoice.invoiceLink,
            };
            return invoice.invoiceLink;
          }
        } catch (error) {
          lastError = error;
          if (isTerminalInvoiceLookupError(error)) throw error;
        }
      }

      if (!receivedResponse && lastError instanceof Error) throw lastError;
      throw new AppError('Telegram invoice is still being prepared', 'timeout');
    },
    [queryClient]
  );

  const readActiveSubscription = useCallback(async (): Promise<boolean> => {
    try {
      const subscription = await queryClient.fetchQuery({
        ...subscriptionQueryOptions.active(POLLING_REQUEST_TIMEOUT_MS),
        staleTime: 0,
        retry: false,
      });
      return Boolean(subscription);
    } catch {
      // Telegram can close the invoice before the webhook is fully processed.
      // Keep polling within the bounded confirmation window.
      return false;
    }
  }, [queryClient]);

  const waitForActiveSubscription = useCallback(async (): Promise<boolean> => {
    for (const delayMs of SUBSCRIPTION_POLL_DELAYS_MS) {
      await delay(delayMs);
      if (await readActiveSubscription()) return true;
    }

    await queryClient.invalidateQueries({ queryKey: subscriptionKeys.active() });
    return Boolean(queryClient.getQueryData(subscriptionKeys.active()));
  }, [queryClient, readActiveSubscription]);

  const probeCheckoutStatus = useCallback(
    async (checkoutId: string): Promise<CheckoutStatusProbe> => {
      try {
        const checkout = await queryClient.fetchQuery(
          subscriptionQueryOptions.checkoutStatus(checkoutId)
        );
        return { kind: 'status', status: checkout.status };
      } catch (error) {
        if (isAppError(error) && (error.status === 400 || error.status === 404)) {
          return { kind: 'missing' };
        }
        return { kind: 'unavailable' };
      }
    },
    [queryClient]
  );

  useEffect(() => {
    if (phase !== 'awaitingConfirmation' && phase !== 'confirmationNeedsSupport') return;

    const checkoutId = pendingCheckoutRef.current?.checkoutId;
    if (!checkoutId) {
      setPhase('idle');
      return;
    }

    let isCurrent = true;
    let isReconciling = false;

    const completeConfirmation = (): void => {
      clearPendingPayment();
      setPhase('idle');
    };

    const applyAction = (action: CheckoutReconciliationAction): void => {
      switch (action.type) {
        case 'reset':
          clearPendingPayment();
          setPhase('idle');
          emitToast({
            type: 'info',
            message: 'Payment was not completed. You can try again.',
          });
          return;
        case 'wait':
          setPhase('awaitingConfirmation');
          return;
        case 'needsSupport':
          setPhase('confirmationNeedsSupport');
          if (!needsSupportNotifiedRef.current) {
            needsSupportNotifiedRef.current = true;
            emitToast({
              type: 'error',
              message:
                'Payment confirmation is taking too long. Contact support before trying again.',
              action: createSupportToastAction(),
              durationMs: 8000,
            });
          }
          return;
        case 'noop':
          return;
      }
    };

    const reconcile = async (): Promise<void> => {
      if (isReconciling) return;
      isReconciling = true;

      try {
        const hasActiveSubscription = await readActiveSubscription();
        if (!isCurrent) return;
        if (hasActiveSubscription) {
          completeConfirmation();
          return;
        }

        const probe = await probeCheckoutStatus(checkoutId);
        if (!isCurrent) return;

        const now = Date.now();
        if (probe.kind === 'status' && probe.status === 'Paid') {
          paidObservedAtRef.current ??= now;
        }

        applyAction(reconcileCheckout(probe, { now, paidObservedAt: paidObservedAtRef.current }));
      } finally {
        isReconciling = false;
      }
    };

    void reconcile();
    const intervalId = window.setInterval(
      () => void reconcile(),
      phase === 'awaitingConfirmation' ? BACKGROUND_CONFIRMATION_POLL_MS : NEEDS_SUPPORT_POLL_MS
    );

    return () => {
      isCurrent = false;
      window.clearInterval(intervalId);
    };
  }, [clearPendingPayment, phase, probeCheckoutStatus, readActiveSubscription]);

  const runSubscriptionFlow = useCallback(async (): Promise<SubscribeWithStarsResult> => {
    const latestStoredPayment = loadStoredPendingPayment(paymentOwnerKey);
    if (
      latestStoredPayment?.stage === 'confirmation' ||
      pendingPaymentStageRef.current === 'confirmation'
    ) {
      if (latestStoredPayment) {
        pendingCheckoutRef.current = {
          checkoutId: latestStoredPayment.checkoutId,
          invoiceLink: null,
        };
        pendingPaymentExpiresAtRef.current = latestStoredPayment.expiresAt;
      }
      pendingPaymentStageRef.current = 'confirmation';
      setPhase('awaitingConfirmation');
      emitToast({
        type: 'info',
        message: 'A previous payment is still being confirmed.',
      });
      throw new AppError('Payment confirmation is pending', 'client');
    }

    if (
      pendingPaymentExpiresAtRef.current !== null &&
      pendingPaymentExpiresAtRef.current <= Date.now()
    ) {
      clearPendingPayment();
    }

    if (!pendingCheckoutRef.current && latestStoredPayment) {
      pendingCheckoutRef.current = {
        checkoutId: latestStoredPayment.checkoutId,
        invoiceLink: null,
      };
      pendingPaymentStageRef.current = latestStoredPayment.stage;
      pendingPaymentExpiresAtRef.current = latestStoredPayment.expiresAt;
    }

    assertInvoiceAvailable();

    let checkout = pendingCheckoutRef.current;
    if (!checkout) {
      setPhase('creatingCheckout');
      checkout = await createCheckout();
      pendingCheckoutRef.current = checkout;
      rememberPendingPayment(checkout.checkoutId, 'checkout');
    }

    let invoiceLink: string;
    try {
      invoiceLink = await waitForInvoiceLink(checkout);
    } catch (error) {
      if (isAppError(error) && (error.status === 400 || error.status === 404)) {
        clearPendingPayment();
      }
      emitToast({
        type: 'error',
        message: 'Payment link could not be prepared. Try again.',
      });
      throw error;
    }

    if (!env.useMock) {
      let activeSubscription;
      try {
        activeSubscription = await queryClient.fetchQuery({
          ...subscriptionQueryOptions.active(),
          staleTime: 0,
          retry: false,
        });
      } catch (error) {
        emitToast({
          type: 'error',
          message: 'Subscription status could not be verified. Try again.',
        });
        throw error;
      }

      if (activeSubscription) {
        clearPendingPayment();
        emitToast({
          type: 'success',
          message: 'Subscription is already active',
        });
        throw new AppError('Subscription is already active', 'client');
      }
    }

    if (!(await ensurePaymentLockOwnership(paymentOwnerKey))) {
      emitToast({
        type: 'info',
        message: 'Payment is already being handled in another window.',
      });
      throw new AppError('Payment lock ownership was lost', 'client');
    }

    rememberPendingPayment(checkout.checkoutId, 'confirmation');
    setPhase('openingInvoice');
    const invoiceStatus = await openSubscriptionInvoice(invoiceLink);

    if (invoiceStatus === 'cancelled') {
      rememberPendingPayment(checkout.checkoutId, 'checkout');
      return {
        checkoutId: checkout.checkoutId,
        invoiceStatus,
        subscriptionActivated: false,
      };
    }

    if (invoiceStatus === 'failed') {
      clearPendingPayment();
      emitToast({
        type: 'error',
        message: 'Payment failed. Try again or contact support.',
      });
      return {
        checkoutId: checkout.checkoutId,
        invoiceStatus,
        subscriptionActivated: false,
      };
    }

    setPhase('confirmingSubscription');
    emitToast({
      type: 'info',
      message: 'Checking payment status...',
    });

    const subscriptionActivated = await waitForActiveSubscription();

    if (subscriptionActivated) {
      clearPendingPayment();
      emitToast({
        type: 'success',
        message: 'Subscription activated',
      });
    } else {
      setPhase('awaitingConfirmation');
      emitToast({
        type: 'info',
        message: 'Payment is still processing. Subscription will update after confirmation.',
        durationMs: 6000,
      });
    }

    return {
      checkoutId: checkout.checkoutId,
      invoiceStatus,
      subscriptionActivated,
    };
  }, [
    clearPendingPayment,
    createCheckout,
    paymentOwnerKey,
    queryClient,
    rememberPendingPayment,
    waitForActiveSubscription,
    waitForInvoiceLink,
  ]);

  const subscribe = useCallback((): Promise<SubscribeWithStarsResult> => {
    if (inFlightRef.current) return inFlightRef.current;

    setPhase('creatingCheckout');
    const operation = runWithPaymentLock(paymentOwnerKey, runSubscriptionFlow).finally(() => {
      inFlightRef.current = null;
      setPhase(() =>
        pendingPaymentStageRef.current === 'confirmation' ? 'awaitingConfirmation' : 'idle'
      );
    });

    inFlightRef.current = operation;
    return operation;
  }, [paymentOwnerKey, runSubscriptionFlow]);

  return useMemo(
    () => ({
      subscribe,
      phase,
      isPending:
        phase !== 'idle' &&
        phase !== 'awaitingConfirmation' &&
        phase !== 'confirmationNeedsSupport',
      isAwaitingConfirmation: phase === 'awaitingConfirmation',
      needsSupport: phase === 'confirmationNeedsSupport',
    }),
    [phase, subscribe]
  );
}
